/**
 * useArmedWindow — Owns the armed timeout that fires ENTER_ARMED or ENTER_MISSED.
 *
 * Within ARMED_WINDOW_MS of scheduledAt → ENTER_ARMED (WaitingScreen countdown).
 * More than MISSED_GRACE_MS past scheduledAt → ENTER_MISSED (MissedScreen).
 * Between scheduledAt and scheduledAt + MISSED_GRACE_MS → ENTER_ARMED immediately
 * so WaitingScreen auto-starts (user opened the app slightly late).
 */
import { useEffect, type Dispatch } from "react";
import type { UIAction } from "../bridge/types";
import { TIMING } from "../constants";

export function useArmedWindow(
  scheduledAt: number | null,
  isArmed: boolean,
  isMissed: boolean,
  dispatchUI: Dispatch<UIAction>,
): void {
  useEffect(() => {
    if (!scheduledAt || isArmed || isMissed) return;
    const msUntilArmed = scheduledAt - Date.now() - TIMING.ARMED_WINDOW_MS;
    if (msUntilArmed <= 0) {
      // Past the armed-window start. Check whether the retreat window is still open.
      if (scheduledAt + TIMING.MISSED_GRACE_MS < Date.now()) {
        // Retreat started more than MISSED_GRACE_MS ago — definitely missed.
        dispatchUI({ type: "ENTER_MISSED" });
      } else {
        // Within grace period — arm it; WaitingScreen will auto-start immediately.
        dispatchUI({ type: "ENTER_ARMED" });
      }
      return;
    }
    const id = setTimeout(
      () => dispatchUI({ type: "ENTER_ARMED" }),
      msUntilArmed,
    );
    return () => clearTimeout(id);
  }, [scheduledAt, isArmed, isMissed, dispatchUI]);
}
